import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";

export default function Assessment() {
  const [qs, setQs] = useState([]); const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({}); const [result, setResult] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/assessment/questions").then(({data}) => setQs(data.questions)); }, []);
  const submit = async () => {
    const payload = Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer: String(answer) }));
    const { data } = await api.post("/assessment/submit", { answers: payload });
    setResult(data);
  };
  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 py-12">
        <div className="max-w-3xl mx-auto px-4">
          <span className="rle-eyebrow">Your practice level</span>
          <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">Estimated level: <span className="text-emerald-700 capitalize">{result.estimated_level.replace("_"," ")}</span></h1>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            <Stat label="Score" value={`${result.score}/${result.total}`} sub={`${result.percentage}%`}/>
            <Stat label="Strengths" value={result.strengths.length ? result.strengths.map(s => s.replace("_"," ")).join(", ") : "—"}/>
            <Stat label="To practise" value={result.to_practise.length ? result.to_practise.map(s => s.replace("_"," ")).join(", ") : "—"}/>
          </div>
          <div className="mt-8 rle-card p-6">
            <div className="font-semibold text-slate-900 mb-2">Suggested starting point</div>
            <p className="text-slate-700 text-sm">Start with a short lesson on <b>{result.to_practise[0]?.replace("_"," ") || "everyday communication"}</b>, then try a real-life mission.</p>
            <p className="text-xs text-slate-500 mt-3">This is an estimated practice level, not an official language certification.</p>
          </div>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => nav("/dashboard")} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="assessment-continue-btn">Go to Dashboard</Button>
            <Link to="/learn"><Button variant="outline" className="rounded-full" data-testid="assessment-browse-btn">Browse lessons</Button></Link>
          </div>
        </div>
      </div>
    );
  }
  if (!qs.length) return <div className="p-10 text-slate-500">Loading…</div>;
  const q = qs[i];
  const answered = answers[q.id] !== undefined;
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-sm text-slate-500 mb-2">Question {i+1} of {qs.length}</div>
        <div className="h-2 rounded-full bg-slate-200 overflow-hidden mb-6"><div className="h-full bg-emerald-700" style={{width: `${(i+1)/qs.length*100}%`}}/></div>
        <div className="rle-card p-6 sm:p-8" data-testid={`assessment-question-${i+1}`}>
          <div className="rle-eyebrow">{q.skill}</div>
          <h2 className="mt-2 font-display font-semibold text-xl text-slate-900">{q.prompt}</h2>
          <div className="mt-6 space-y-2">
            {q.options.map((opt, idx) => (
              <button key={idx} onClick={() => setAnswers({...answers, [q.id]: idx})}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${answers[q.id] === idx ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                data-testid={`assessment-option-${i+1}-${idx}`}>
                <span className="font-mono text-xs text-slate-500 mr-2">{String.fromCharCode(65+idx)}</span>{opt}
              </button>
            ))}
          </div>
          <div className="mt-8 flex justify-between">
            <Button variant="outline" className="rounded-full" onClick={() => setI(Math.max(0, i-1))} disabled={i===0} data-testid="assessment-prev-btn">Back</Button>
            {i < qs.length - 1 ? (
              <Button onClick={() => setI(i+1)} disabled={!answered} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="assessment-next-btn">Next</Button>
            ) : (
              <Button onClick={submit} disabled={!answered} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="assessment-submit-btn"><CheckCircle2 size={16} className="mr-1"/>See my results</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, sub }) => (
  <div className="rle-card p-5">
    <div className="rle-eyebrow">{label}</div>
    <div className="mt-2 font-display font-bold text-xl text-slate-900 capitalize">{value}</div>
    {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
  </div>
);
